import { DateField, DateTimeField, TextField } from "@dgs/core";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { ApplicationTypeFilterSelectField } from "../ui/ApplicationTypeFilterSelectField";
import { ApplicationTypeFilterSingleSelectField } from "../ui/ApplicationTypeFilterSingleSelectField";
import { CountryMultiSelectField, CountrySelectField } from "../ui/CountrySelectField";
import { EventCategoryMultiSelectField } from "../ui/EventCategoryMultiSelectField";
import { EventCategorySelectField } from "../ui/EventCategorySelectField";
import { EventFormatMultiSelectField } from "../ui/EventFormatMultiSelectField";
import { EventFormatSingleSelectField } from "../ui/EventFormatSingleSelectField";
import { EventParticipationStatusSelectField } from "../ui/EventParticipationStatusSelectField";
import { EventTypeMultiSelectField } from "../ui/EventTypeMultiSelectField";
import { EventTypeSingleSelectField } from "../ui/EventTypeSingleSelectField";
import { IndustryMultiSelectField } from "../ui/IndustryMultiSelectField";
import { LocationMultiSelectField, LocationSelectFields } from "../ui/LocationSelectFields";
import { OrganizationalUnitMultiSelectField } from "../ui/OrganizationalUnitMultiSelectField";
import { OrganizationalUnitSingleSelectField } from "../ui/OrganizationalUnitSingleSelectField";
import { OrganizerMultiSelectField } from "../ui/OrganizerMultiSelectField";
import { OrganizerSingleSelectField } from "../ui/OrganizerSingleSelectField";
import { RegionMultiSelectField } from "../ui/RegionMultiSelectField";
import { RegionSelectField } from "../ui/RegionSelectField";
import { TagMultiSelectField } from "../ui/TagSelectField";
import { TargetGroupMultiSelectField } from "../ui/TargetGroupMultiSelectField";
import { TargetGroupSelectField } from "../ui/TargetGroupSelectField";
import { FilterOperator, FilterableEntity, FilterableField } from "./filterTypes";

interface Props {
	name: string;
	operator: FilterOperator;
	entity: FilterableEntity;
	field: FilterableField;
}

export const FilterValueField: FC<Props> = ({ name, operator, entity, field }) => {
	const { t } = useTranslation();
	const props = { label: t("Value"), name: name, required: true };

	switch (entity) {
		case "EVENT":
			return <EventValueField field={field} operator={operator} props={props} />;
		case "APPLICATION":
			return <ApplicationValueField field={field} operator={operator} props={props} />;
		default:
			return null;
	}
};

const EventValueField: FC<{
	field: FilterableField;
	operator: FilterOperator;
	props: { label: string; name: string };
}> = ({ field, operator, props }) => {
	const { t } = useTranslation();

	switch (field) {
		case "END":
		case "START":
			switch (operator) {
				case "BETWEEN":
					return (
						<div>
							<DateTimeField label={t("Start")} name={`${props.name}.start`} />
							<DateTimeField label={t("End")} name={`${props.name}.end`} />
						</div>
					);
				default:
					return <DateTimeField {...props} />;
			}
		case "TAGS":
			return <TagMultiSelectField {...props} />;
		case "FORMAT":
			return hasOptions(operator) ? (
				<EventFormatMultiSelectField {...props} />
			) : (
				<EventFormatSingleSelectField {...props} />
			);
		case "EVENT_TYPE":
			return hasOptions(operator) ? (
				<EventTypeMultiSelectField {...props} />
			) : (
				<EventTypeSingleSelectField {...props} />
			);
		case "REGION":
			return hasOptions(operator) ? <RegionMultiSelectField {...props} /> : <RegionSelectField {...props} />;
		case "INDUSTRIES":
			return <IndustryMultiSelectField {...props} />;
		case "PRIMARY_ORGANIZATIONAL_UNIT":
			return hasOptions(operator) ? (
				<OrganizationalUnitMultiSelectField {...props} />
			) : (
				<OrganizationalUnitSingleSelectField {...props} />
			);
		case "ORGANIZATIONAL_UNITS":
			return <OrganizationalUnitMultiSelectField {...props} />;
		case "TARGET_GROUP":
			return hasOptions(operator) ? <TargetGroupMultiSelectField {...props} /> : <TargetGroupSelectField {...props} />;
		case "EVENT_CATEGORY":
			return hasOptions(operator) ? (
				<EventCategoryMultiSelectField {...props} />
			) : (
				<EventCategorySelectField {...props} />
			);
		case "ORGANIZER":
			return hasOptions(operator) ? (
				<OrganizerMultiSelectField {...props} />
			) : (
				<OrganizerSingleSelectField {...props} />
			);
		case "ADDRESS_CITY":
			return <TextField {...props} />;
		case "ADDRESS_COUNTRY":
			return hasOptions(operator) ? <CountryMultiSelectField {...props} /> : <CountrySelectField {...props} />;
		case "LOCATION":
			return hasOptions(operator) ? <LocationMultiSelectField {...props} /> : <LocationSelectFields {...props} />;
		case "EVENT_PARTICIPATION_STATUS":
			return <EventParticipationStatusSelectField {...props} />;
		default:
			return null;
	}
};

const ApplicationValueField: FC<{
	field: FilterableField;
	operator: FilterOperator;
	props: { label: string; name: string };
}> = ({ field, operator, props }) => {
	const { t } = useTranslation();

	switch (field) {
		case "SETUP_DATE":
		case "DELETION_DATE":
			switch (operator) {
				case "BETWEEN":
					return (
						<div>
							<DateField label={t("Start")} name={`${props.name}.start`} required />
							<DateField label={t("End")} name={`${props.name}.end`} required />
						</div>
					);
				default:
					return <DateField {...props} required />;
			}
		case "TYPE":
			return hasOptions(operator) ? (
				<ApplicationTypeFilterSelectField {...props} />
			) : (
				<ApplicationTypeFilterSingleSelectField {...props} />
			);
		default:
			return null;
	}
};

const hasOptions = (operator: FilterOperator): boolean => {
	return (
		operator === "IN" ||
		operator === "NOT_IN" ||
		operator === "NOT_IN_RELATION" ||
		operator === "IN_RELATION" ||
		operator === "NOT_IN_ADDRESSES_COUNTRY" ||
		operator === "IN_ADDRESSES_COUNTRY" ||
		operator === "NOT_IN_APPLICATION_TYPE" ||
		operator === "IN_APPLICATION_TYPE"
	);
};
